import React, { useEffect, useRef, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Upload, Trash2, FileText, Shield, CalendarClock, AlertTriangle, Mail, Sparkles, Check, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useLang } from '../context/LangContext';
import { SmsSettings } from '../components/SmsSettings';
import { queueUpload, listPending, removePending } from '../lib/offlineQueue';
import { Camera, WifiOff, CloudUpload } from 'lucide-react';

const CATEGORIES = ['Identity & Residency', 'Medical & Disability Reports', 'Financial & Tax Statements', 'Government Forms & Notices'];

const APPEAL_TAGS = [['odsp:internal_review', 'ODSP – Internal Review'], ['odsp:sbt', 'ODSP – Tribunal appeal'], ['dtc:second_review', 'DTC – CRA review'], ['dtc:objection', 'DTC – Objection'], ['cppd:reconsideration', 'CPP-D – Reconsideration'], ['cppd:sst', 'CPP-D – Tribunal']];

const daysUntil = (iso) => Math.ceil((new Date(iso) - new Date()) / 86400000);

const ExpiryBadge = ({ d }) => {
  if (!d.expiry_date) return null;
  const days = daysUntil(d.expiry_date);
  if (days < 0) return <Badge className="bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5] border text-[10px]" data-testid={`expiry-badge-${d.doc_id}`}>Expired {Math.abs(days)}d ago</Badge>;
  if (days <= 90) return <Badge className="bg-[#FFF4E5] text-[#663C00] border-[#FFE0B2] border text-[10px]" data-testid={`expiry-badge-${d.doc_id}`}>Renew in {days}d</Badge>;
  return <Badge variant="outline" className="text-[10px]" data-testid={`expiry-badge-${d.doc_id}`}>Expires {new Date(d.expiry_date).toLocaleDateString()}</Badge>;
};

export default function Documents() {
  const { t } = useLang();
  const [docs, setDocs] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [expiry, setExpiry] = useState('');
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [autoSort, setAutoSort] = useState(() => localStorage.getItem('doc_auto_sort') !== 'off');
  const fileRef = useRef();
  const cameraRef = useRef();
  const [pending, setPending] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);

  const refreshPending = () => listPending().then(setPending).catch(() => {});
  const flushPending = async () => {
    const items = await listPending().catch(() => []);
    for (const it of items) {
      try {
        const fd = new FormData(); fd.append('file', it.blob, it.filename); fd.append('category', it.category); fd.append('label', it.filename); fd.append('expiry_date', it.expiry_date || '');
        await api.post('/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        await removePending(it.id);
        toast.success(`Uploaded "${it.filename}" from your offline queue`);
      } catch { break; }
    }
    refreshPending(); load();
  };
  useEffect(() => {
    refreshPending();
    const on = () => { setOnline(true); flushPending(); }; const off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    if (navigator.onLine) flushPending();
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const onCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const filename = `photo-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')}.jpg`;
    if (!navigator.onLine) {
      await queueUpload({ blob: file, filename, category, expiry_date: expiry });
      refreshPending(); toast.message('Saved on this device — it will upload automatically when you\u2019re back online');
      cameraRef.current.value = ''; return;
    }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file, filename); fd.append('category', category); fd.append('label', filename); fd.append('expiry_date', expiry);
      const { data: created } = await api.post('/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await load(); toast.success('Photo uploaded');
      if (autoSort) autoAnalyze(created);
    } catch (err) {
      if (!err.response) { await queueUpload({ blob: file, filename, category, expiry_date: expiry }); refreshPending(); toast.message('No connection — photo saved on this device and will upload later'); }
      else toast.error('Upload failed');
    } finally { setUploading(false); cameraRef.current.value = ''; }
  };

  const toggleAutoSort = (v) => { setAutoSort(v); localStorage.setItem('doc_auto_sort', v ? 'on' : 'off'); };

  const autoAnalyze = async (created) => {
    setAnalyzing(created.doc_id);
    try {
      const { data } = await api.post(`/documents/${created.doc_id}/analyze`);
      const patch = { category: data.category, label: data.label };
      if (data.expiry_date && !created.expiry_date) patch.expiry_date = data.expiry_date;
      if (data.confidence >= 0.7) {
        const { data: updated } = await api.patch(`/documents/${created.doc_id}`, patch);
        setDocs((c) => c.map((x) => (x.doc_id === created.doc_id ? { ...updated, auto_sorted: true } : x)));
        window.dispatchEvent(new Event('docs-changed'));
        toast.success(`Filed as "${data.label}" under ${data.category}${data.expiry_date ? ` · expires ${data.expiry_date}` : ''}`);
      } else {
        setSuggestions((s) => ({ ...s, [created.doc_id]: data }));
        toast.message('AI wasn\u2019t sure — review the suggestion on the card');
      }
    } catch { toast.error('Auto-sort could not read this file; you can sort it manually'); }
    finally { setAnalyzing(null); }
  };

  const analyze = async (d) => {
    setAnalyzing(d.doc_id);
    try {
      const { data } = await api.post(`/documents/${d.doc_id}/analyze`);
      setSuggestions((s) => ({ ...s, [d.doc_id]: data }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Auto-sort failed'); }
    finally { setAnalyzing(null); }
  };

  const applySuggestion = async (d) => {
    const s = suggestions[d.doc_id];
    const { data } = await api.patch(`/documents/${d.doc_id}`, { category: s.category, label: s.label, expiry_date: s.expiry_date || undefined });
    setDocs((c) => c.map((x) => (x.doc_id === d.doc_id ? data : x)));
    setSuggestions((m) => { const n = { ...m }; delete n[d.doc_id]; return n; });
    window.dispatchEvent(new Event('docs-changed'));
    toast.success('Applied AI suggestion');
  };

  const load = async () => {
    const { data } = await api.get('/documents');
    setDocs(data.documents);
    api.get('/reminders').then((r) => setReminders(r.data.reminders)).catch(() => {});
    window.dispatchEvent(new Event('docs-changed'));
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', category);
      fd.append('label', file.name);
      fd.append('expiry_date', expiry);
      const { data: created } = await api.post('/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await load();
      setExpiry('');
      toast.success('Document uploaded');
      if (autoSort && (file.type.startsWith('image/') || file.type === 'application/pdf')) autoAnalyze(created);
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); fileRef.current.value = ''; }
  };

  const setAppealTag = async (d, value) => {
    const { data } = await api.patch(`/documents/${d.doc_id}`, { appeal_tag: value === 'none' ? '' : value });
    setDocs((c) => c.map((x) => (x.doc_id === d.doc_id ? data : x)));
    toast.success(value === 'none' ? 'Removed from appeal evidence' : 'Tagged as appeal evidence');
  };

  const setDocExpiry = async (d, value) => {
    const { data } = await api.patch(`/documents/${d.doc_id}`, { expiry_date: value });
    setDocs((c) => c.map((x) => (x.doc_id === d.doc_id ? data : x)));
    window.dispatchEvent(new Event('docs-changed'));
    toast.success(value ? 'Expiry date saved — we\u2019ll flag it 90 days before' : 'Expiry date removed');
  };

  const del = async (d) => {
    await api.delete(`/documents/${d.doc_id}`);
    setDocs((c) => c.filter((x) => x.doc_id !== d.doc_id));
    window.dispatchEvent(new Event('docs-changed'));
    toast.success('Deleted');
  };

  const grouped = CATEGORIES.map((c) => ({ c, items: docs.filter((d) => d.category === c) }));
  const expiringSoon = docs.filter((d) => d.expiry_date && daysUntil(d.expiry_date) <= 90).sort((a, b) => daysUntil(a.expiry_date) - daysUntil(b.expiry_date));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{fontFamily: 'Lexend'}}>{t('My Documents')}</h1>
          <p className="text-muted-foreground mt-2 flex items-center gap-2"><Shield className="w-4 h-4 text-primary" /> {t('Stored securely. Only you can see them.')}</p>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5" data-testid="email-reminder-note"><Mail className="w-3.5 h-3.5" /> {t('Add an expiry date and we\'ll email you at 90, 30 and 7 days before renewal.')}</p>
        </div>
      </div>

      <SmsSettings />

      {(!online || pending.length > 0) && (
        <div className="mb-6 rounded-xl border border-[#B8D5E5] bg-[#EBF3FA] p-4 text-sm text-[#13426B]" role="status" data-testid="offline-queue">
          <div className="font-semibold flex items-center gap-2">{online ? <CloudUpload className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />} {online ? `${pending.length} photo${pending.length === 1 ? '' : 's'} waiting to upload` : 'You\u2019re offline — photos you take are saved on this device and upload automatically later'}</div>
          {pending.length > 0 && <ul className="mt-1.5 text-xs">{pending.map((p) => <li key={p.id} data-testid={`pending-${p.id}`}>• {p.filename} · {p.category}</li>)}</ul>}
          {online && pending.length > 0 && <Button size="sm" variant="outline" className="mt-2 bg-white" onClick={flushPending} data-testid="offline-flush">Upload now</Button>}
        </div>
      )}

      {expiringSoon.length > 0 && (
        <div className="mb-6 rounded-xl border border-[#FFE0B2] bg-[#FFF4E5] p-4" role="alert" data-testid="expiry-banner">
          <div className="flex items-center gap-2 font-semibold text-[#663C00]"><AlertTriangle className="w-4 h-4" /> {expiringSoon.length} document{expiringSoon.length === 1 ? '' : 's'} need renewal attention</div>
          <ul className="mt-2 text-sm text-[#663C00] space-y-1">
            {expiringSoon.map((d) => { const days = daysUntil(d.expiry_date); return <li key={d.doc_id} data-testid={`expiry-item-${d.doc_id}`}>• <strong>{d.label}</strong> — {days < 0 ? `expired ${Math.abs(days)} days ago` : `expires in ${days} days`} ({new Date(d.expiry_date).toLocaleDateString()}). Start the renewal now so your applications aren't delayed.</li>; })}
          </ul>
        </div>
      )}

      <Card className="p-5 mb-8 bg-card">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
          <div className="flex-1">
            <label className="text-sm font-medium block mb-1.5">{t('Category')}</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-11" data-testid="doc-category-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card">
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(c)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:w-56">
            <label htmlFor="doc-expiry" className="text-sm font-medium block mb-1.5 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> {t('Expiry date (optional)')}</label>
            <Input id="doc-expiry" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="h-11" data-testid="doc-expiry-input" />
          </div>
          <div className="flex items-center gap-2 h-11 px-3 rounded-md border border-border bg-background">
            <Switch id="auto-sort" checked={autoSort} onCheckedChange={toggleAutoSort} data-testid="auto-sort-toggle" />
            <label htmlFor="auto-sort" className="text-sm font-medium flex items-center gap-1.5 whitespace-nowrap"><Sparkles className="w-3.5 h-3.5 text-primary" /> {t('Auto-sort on upload')}</label>
          </div>
          <div>
            <input ref={fileRef} type="file" className="hidden" onChange={onUpload} accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" data-testid="doc-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doc-upload-btn" className="h-11 bg-primary hover:bg-primary/90 text-primary-foreground">
              <Upload className="w-4 h-4 mr-2" /> {uploading ? t('Uploading…') : t('Upload document')}
            </Button>
            <input ref={cameraRef} type="file" className="hidden" onChange={onCapture} accept="image/*" capture="environment" data-testid="doc-camera-input" />
            <Button onClick={() => cameraRef.current?.click()} disabled={uploading} variant="outline" data-testid="doc-camera-btn" className="h-11 ml-2" aria-label="Take a photo of a document">
              <Camera className="w-4 h-4 mr-2" /> Photo
            </Button>
          </div>
        </div>
      </Card>

      <div className="space-y-6">
        {grouped.map((g) => (
          <div key={g.c}>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-lg font-semibold" style={{fontFamily: 'Lexend'}}>{t(g.c)}</h2>
              <Badge variant="outline">{g.items.length}</Badge>
            </div>
            {g.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('No documents yet.')}</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {g.items.map((d) => (
                  <Card key={d.doc_id} className="p-4 bg-card" data-testid={`doc-card-${d.doc_id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-primary" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{d.label}</div>
                        <div className="text-xs text-muted-foreground">{Math.round(d.size/1024)} KB · {new Date(d.uploaded_at).toLocaleDateString()}</div>
                        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap"><ExpiryBadge d={d} />{d.auto_sorted && <Badge variant="outline" className="text-[10px] border-primary/40 text-primary" data-testid={`auto-sorted-${d.doc_id}`}><Sparkles className="w-2.5 h-2.5 mr-1" /> AI filed</Badge>}{analyzing === d.doc_id && <Badge variant="outline" className="text-[10px] animate-pulse" data-testid={`analyzing-${d.doc_id}`}>Reading…</Badge>}</div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => del(d)} aria-label="Delete document" data-testid={`doc-del-${d.doc_id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <label htmlFor={`exp-${d.doc_id}`} className="text-xs text-muted-foreground whitespace-nowrap">Expires</label>
                      <Input id={`exp-${d.doc_id}`} type="date" value={d.expiry_date || ''} onChange={(e) => setDocExpiry(d, e.target.value)} className="h-9 text-xs" data-testid={`doc-expiry-${d.doc_id}`} />
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-xs text-muted-foreground whitespace-nowrap">Evidence for</label>
                      <Select value={d.appeal_tag || 'none'} onValueChange={(v) => setAppealTag(d, v)}>
                        <SelectTrigger className="h-9 text-xs" data-testid={`doc-appeal-tag-${d.doc_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-card"><SelectItem value="none">— not an appeal exhibit —</SelectItem>{APPEAL_TAGS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {suggestions[d.doc_id] ? (
                      <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs" data-testid={`ai-suggestion-${d.doc_id}`}>
                        <div className="font-semibold flex items-center gap-1.5 text-primary"><Sparkles className="w-3.5 h-3.5" /> AI suggests · {Math.round(suggestions[d.doc_id].confidence * 100)}% sure</div>
                        <div className="mt-1.5"><strong>{suggestions[d.doc_id].label}</strong> → {suggestions[d.doc_id].category}{suggestions[d.doc_id].expiry_date ? ` · expires ${suggestions[d.doc_id].expiry_date}` : ''}</div>
                        <p className="text-muted-foreground mt-1">{suggestions[d.doc_id].reasoning}</p>
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" className="h-8 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => applySuggestion(d)} data-testid={`ai-apply-${d.doc_id}`}><Check className="w-3.5 h-3.5 mr-1" /> Apply</Button>
                          <Button size="sm" variant="outline" className="h-8" onClick={() => setSuggestions((m) => { const n = { ...m }; delete n[d.doc_id]; return n; })} data-testid={`ai-dismiss-${d.doc_id}`}><X className="w-3.5 h-3.5 mr-1" /> Dismiss</Button>
                        </div>
                      </div>
                    ) : (
                      (d.content_type?.startsWith('image/') || d.content_type === 'application/pdf' || /\.(pdf|png|jpe?g|webp)$/i.test(d.filename)) && (
                        <Button size="sm" variant="outline" className="mt-3 w-full h-8 text-xs" onClick={() => analyze(d)} disabled={analyzing === d.doc_id} data-testid={`ai-sort-${d.doc_id}`}><Sparkles className={`w-3.5 h-3.5 mr-1.5 ${analyzing === d.doc_id ? 'animate-pulse' : ''}`} /> {analyzing === d.doc_id ? 'Reading document…' : t('Auto-sort with AI')}</Button>
                      )
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {reminders.length > 0 && (
        <div className="mt-10" data-testid="reminder-log">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{fontFamily: 'Lexend'}}><Mail className="w-4 h-4 text-primary" /> Reminder emails sent</h2>
          <ul className="space-y-1.5">
            {reminders.slice(0, 10).map((r) => (
              <li key={r.reminder_id} className="text-sm flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2" data-testid={`reminder-${r.reminder_id}`}>
                <span><strong>{r.label}</strong> — {r.stage} reminder</span>
                <span className="text-xs text-muted-foreground">{new Date(r.sent_at).toLocaleDateString()} · {r.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
